import { useRef, useState } from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  Alert,
  Skeleton,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  useTheme,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { TimePicker } from '@mui/x-date-pickers/TimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import DownloadIcon from '@mui/icons-material/Download'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PaidIcon from '@mui/icons-material/Paid'
import WeekendIcon from '@mui/icons-material/Weekend'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import BugReportIcon from '@mui/icons-material/BugReport'
import ManageSearchIcon from '@mui/icons-material/ManageSearch'
import dayjs, { type Dayjs } from 'dayjs'
import duration from 'dayjs/plugin/duration'
import { useTranslation } from 'react-i18next'
import {
  useSessionSummary,
  useUpdateIncidentNotes,
  useCreateIncident,
  useDeleteIncident,
} from '../hooks/useSessions'
import { useAccount } from '../hooks/useAccount'
import { useAdminUsers, useAdminSessionSummary } from '../hooks/useAdmin'
import { downloadExcel } from '../api/report'
import { downloadAdminExcel } from '../api/admin'
import type { SessionInfoResponse, IncidentResponse } from '../types'
import SessionCalendar from '../components/SessionCalendar'
import { formatDuration } from '../lib/sessionTime'

dayjs.extend(duration)

// ─── Edit incident notes dialog ───────────────────────────────────────────────

function EditIncidentDialog({
  incident,
  tz,
  onClose,
}: {
  incident: IncidentResponse | null
  tz: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const borderColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'
  const { mutate: update, isPending } = useUpdateIncidentNotes()
  const [notes, setNotes] = useState(incident?.notes ?? '')
  const [invMin, setInvMin] = useState(String(incident?.investigationMinutes ?? 0))

  const prevIncidentId = useRef(incident?.incidentId)
  if (incident?.incidentId !== prevIncidentId.current) {
    prevIncidentId.current = incident?.incidentId
    setNotes(incident?.notes ?? '')
    setInvMin(String(incident?.investigationMinutes ?? 0))
  }

  if (!incident) return null

  const handleSave = () => {
    update(
      { incidentId: incident.incidentId, notes: notes || undefined, investigationMinutes: parseInt(invMin) || 0 },
      { onSuccess: onClose }
    )
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3, border: `1px solid ${borderColor}` } } }}>
      <DialogTitle sx={{ pb: 1 }}>
        {t('sessions.dialog.incidentNotes')}
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {incident.emailSubject ?? t('sessions.manualIncident')} · {dayjs(incident.startedAt).tz(tz).format('DD MMM HH:mm')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField
            label={t('sessions.dialog.notes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 300))}
            fullWidth size="small" multiline rows={3}
            helperText={t('sessions.dialog.notesHelper', { count: notes.length })}
            slotProps={{ htmlInput: { maxLength: 300 } }}
          />
          <TextField
            label={t('sessions.dialog.investigationTime')}
            type="number"
            value={invMin}
            onChange={(e) => setInvMin(e.target.value)}
            fullWidth size="small"
            helperText={t('sessions.dialog.investigationHelper')}
            slotProps={{ htmlInput: { min: 0, max: 1440 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={isPending}>{t('sessions.dialog.cancel')}</Button>
        <Button variant="contained" onClick={handleSave} disabled={isPending}>
          {isPending ? t('sessions.dialog.saving') : t('sessions.dialog.save')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** All calendar days from session start to session end (inclusive), in the user's timezone.
 *  Excludes the last day when the session ends exactly at midnight — no valid time to pick. */
function getSessionDates(session: { startTime: string; endTime?: string }, tz: string): Dayjs[] {
  const dates: Dayjs[] = []
  let d = dayjs(session.startTime).tz(tz).startOf('day')
  const sessionEndTz = session.endTime ? dayjs(session.endTime).tz(tz) : dayjs(session.startTime).tz(tz)
  // If end is exactly midnight, that day has no pickable time (e.g. 10 Mar 00:00 → exclude 10 Mar)
  const endDay = sessionEndTz.isSame(sessionEndTz.startOf('day'))
    ? sessionEndTz.startOf('day').subtract(1, 'day')
    : sessionEndTz.startOf('day')
  while (!d.isAfter(endDay)) {
    dates.push(d)
    d = d.add(1, 'day')
  }
  return dates
}

// ─── Create incident dialog ───────────────────────────────────────────────────

function CreateIncidentDialog({
  session,
  tz,
  onClose,
}: {
  session: SessionInfoResponse | null
  tz: string
  onClose: () => void
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const borderColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'
  const { mutate: create, isPending } = useCreateIncident()
  const sessionKey = session?.sessionId ?? session?.startTime
  // Seed from the session on mount too: the dialog is remounted via `key` in the same render
  // the session arrives, so the "session changed" branch below never sees a transition.
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(
    () => (session ? dayjs(session.startTime).tz(tz).startOf('day') : null)
  )
  const [selectedTime, setSelectedTime] = useState<Dayjs | null>(
    () => (session ? dayjs(session.startTime).tz(tz) : null)
  )
  const [notes, setNotes] = useState('')
  const [invMin, setInvMin] = useState('0')

  const prevSessionKey = useRef(sessionKey)
  if (sessionKey !== prevSessionKey.current) {
    prevSessionKey.current = sessionKey
    if (session) {
      const start = dayjs(session.startTime).tz(tz)
      setSelectedDate(start.startOf('day'))
      setSelectedTime(start)
    } else {
      setSelectedDate(null)
      setSelectedTime(null)
    }
    setNotes('')
    setInvMin('0')
  }

  if (!session) return null

  const sessionStart  = dayjs(session.startTime).tz(tz)
  const sessionEnd    = dayjs(session.endTime).tz(tz)
  const sessionDates  = getSessionDates(session, tz)
  const minTime = selectedDate?.isSame(sessionStart, 'day') ? sessionStart : undefined
  const maxTime = selectedDate?.isSame(sessionEnd,   'day') ? sessionEnd   : undefined
  const incidentTime  = selectedDate && selectedTime
    ? selectedDate.set('hour', selectedTime.hour()).set('minute', selectedTime.minute()).set('second', 0)
    : null

  const handleCreate = () => {
    if (!incidentTime) return
    create(
      { startedAt: incidentTime.toISOString(),
        notes: notes || undefined, investigationMinutes: parseInt(invMin) || 0 },
      { onSuccess: onClose }
    )
  }

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3, border: `1px solid ${borderColor}` } } }}>
      <DialogTitle sx={{ pb: 1 }}>
        {t('sessions.dialog.addIncident')}
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          {session.sessionId ? (session.title ?? t('sessions.dialog.onCallSession')) : t('sessions.table.unmatchedIncident')} · {sessionStart.format('DD MMM HH:mm')}{session.endTime ? ` – ${sessionEnd.format('DD MMM HH:mm')}` : ''}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Stack direction="row" spacing={1.5}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel id="inc-date-label">{t('sessions.dialog.date')}</InputLabel>
              <Select
                labelId="inc-date-label"
                label={t('sessions.dialog.date')}
                value={selectedDate ? selectedDate.format('YYYY-MM-DD') : ''}
                onChange={(e) => {
                  const d = dayjs.tz(e.target.value as string, tz)
                  setSelectedDate(d)
                  // First valid time on the new day
                  const newMin = d.isSame(sessionStart, 'day') ? sessionStart : d.startOf('day')
                  const newMax = d.isSame(sessionEnd,   'day') ? sessionEnd   : d.endOf('day')
                  // Keep current time if it still falls inside the valid range; otherwise reset to start
                  if (!selectedTime || selectedTime.isBefore(newMin) || selectedTime.isAfter(newMax)) {
                    setSelectedTime(newMin)
                  }
                }}
              >
                {sessionDates.map((d) => (
                  <MenuItem key={d.format('YYYY-MM-DD')} value={d.format('YYYY-MM-DD')}>
                    {d.format('DD MMM (ddd)')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TimePicker
              label={t('sessions.dialog.time')}
              value={selectedTime}
              onChange={(v) => setSelectedTime(v)}
              minTime={minTime}
              maxTime={maxTime}
              timezone={tz}
              ampm={false}
              slotProps={{ textField: { size: 'small', sx: { flex: 1 } } }}
            />
          </Stack>
          <TextField
            label={t('sessions.dialog.notes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value.slice(0, 300))}
            fullWidth size="small" multiline rows={3}
            helperText={t('sessions.dialog.notesCount', { count: notes.length })}
            slotProps={{ htmlInput: { maxLength: 300 } }}
          />
          <TextField
            label={t('sessions.dialog.investigationTime')}
            type="number"
            value={invMin}
            onChange={(e) => setInvMin(e.target.value)}
            fullWidth size="small"
            slotProps={{ htmlInput: { min: 0, max: 1440 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit" disabled={isPending}>{t('sessions.dialog.cancel')}</Button>
        <Button variant="contained" onClick={handleCreate} disabled={isPending || !incidentTime}>
          {isPending ? t('sessions.dialog.creating') : t('sessions.dialog.create')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Session incidents popup ──────────────────────────────────────────────────

function SessionIncidentsDialog({
  session,
  tz,
  onClose,
  onAdd,
  onEdit,
}: {
  session: SessionInfoResponse | null
  tz: string
  onClose: () => void
  onAdd: () => void
  onEdit: (incident: IncidentResponse) => void
}) {
  const { t } = useTranslation()
  const theme = useTheme()
  const borderColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'
  const dividerColor = theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'
  const { mutate: deleteInc, isPending: deleting } = useDeleteIncident()
  // incidentId that is "armed" for deletion (first click arms, second click confirms)
  const [armedId, setArmedId] = useState<string | null>(null)

  if (!session) return null
  const sessionIncidents = [...(session.incidents ?? [])]
    .sort((a, b) => dayjs(a.startedAt).diff(dayjs(b.startedAt)))

  const handleDeleteClick = (incidentId: string) => {
    if (armedId === incidentId) {
      deleteInc(incidentId, { onSuccess: () => setArmedId(null) })
    } else {
      setArmedId(incidentId)
    }
  }

  return (
    <Dialog open onClose={() => { setArmedId(null); onClose() }} maxWidth="sm" fullWidth
      slotProps={{ paper: { sx: { borderRadius: 3, border: `1px solid ${borderColor}` } } }}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <BugReportIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
          <span>{t('sessions.dialog.incidentsTitle')}</span>
          <Typography component="span" variant="body2" color="text.secondary">
            · {session.sessionId ? (session.title ?? t('sessions.dialog.onCallSession')) : t('sessions.table.unmatchedIncident')}
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" mt={0.25}>
          {dayjs(session.startTime).tz(tz).format('DD MMM HH:mm')}{session.endTime ? ` – ${dayjs(session.endTime).tz(tz).format('DD MMM HH:mm')}` : ''}
        </Typography>
      </DialogTitle>
      <Divider sx={{ borderColor: dividerColor }} />
      <DialogContent sx={{ px: 2, py: 0, minHeight: 80 }}>
        {sessionIncidents.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
            {t('sessions.dialog.noIncidents')}
          </Typography>
        ) : (
          <Stack divider={<Divider sx={{ borderColor: dividerColor }} />}>
            {sessionIncidents.map((inc) => {
              const armed = armedId === inc.incidentId
              return (
                <Stack key={inc.incidentId} direction="row" alignItems="flex-start" spacing={1.5} py={1.5}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={0.25} flexWrap="wrap">
                      <Typography variant="body2" fontWeight={500}>
                        {dayjs(inc.startedAt).tz(tz).format('DD MMM HH:mm')}
                      </Typography>
                      <Chip
                        label={t(`incidentStatus.${inc.status}`)}
                        size="small"
                        color={
                          inc.status === 'ACTIVE'    ? 'error'   :
                          inc.status === 'CONFIRMED' ? 'success' :
                          inc.status === 'STALLED'   ? 'warning' :
                          inc.status === 'TIMEOUT'   ? 'info'    : 'default'
                        }
                        sx={{ height: 18, fontSize: 10, color: '#fff' }}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary" display="block" noWrap>
                      {inc.emailSubject ?? (inc.notes ? `${t('sessions.manual')}: ${inc.notes.slice(0, 60)}` : t('sessions.manualIncident'))}
                    </Typography>
                    {(inc.investigationMinutes ?? 0) > 0 && (
                      <Typography variant="caption" color="text.disabled" display="block">
                        {formatDuration(inc.investigationMinutes!)} {t('sessions.investigated')}
                      </Typography>
                    )}
                  </Box>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.25 }}>
                    {armed && (
                      <Typography variant="caption" color="error.main" sx={{ whiteSpace: 'nowrap' }}>
                        {t('sessions.dialog.deleteConfirm')}
                      </Typography>
                    )}
                    <Tooltip title={armed ? t('sessions.dialog.confirmDeletion') : t('sessions.dialog.deleteIncidentTooltip')}>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={deleting}
                        onClick={() => handleDeleteClick(inc.incidentId)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('sessions.dialog.editNotesTooltip')}>
                      <IconButton size="small" onClick={() => { setArmedId(null); onEdit(inc) }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Stack>
              )
            })}
          </Stack>
        )}
      </DialogContent>
      <Divider sx={{ borderColor: dividerColor }} />
      <DialogActions sx={{ px: 2, py: 1.5, justifyContent: 'space-between' }}>
        <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={onAdd}>
          {t('sessions.dialog.addIncidentBtn')}
        </Button>
        <Button size="small" color="inherit" onClick={() => { setArmedId(null); onClose() }}>{t('sessions.dialog.close')}</Button>
      </DialogActions>
    </Dialog>
  )
}

// ─── Stat card ────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon, color = 'primary.main', loading }: {
  label: string; value: string; icon: React.ReactNode; color?: string; loading?: boolean
}) {
  return (
    <Card sx={{ flex: 1 }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <Box sx={{ color, fontSize: 32, display: 'flex' }}>{icon}</Box>
        <Box>
          <Typography variant="caption" color="text.secondary">{label}</Typography>
          <Typography variant="h5" fontWeight={700}>
            {loading ? <Skeleton width={80} /> : value}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function ReportPage() {
  const { t, i18n } = useTranslation()
  const { data: account } = useAccount()
  const userTz = account?.timezone ?? 'UTC'
  const isAdmin = account?.role === 'ADMIN'
  const { data: adminUsers } = useAdminUsers(isAdmin)

  const [selectedMonth, setSelectedMonth] = useState<Dayjs>(() => dayjs().tz(userTz).startOf('month'))
  const [selectedUserId, setSelectedUserId] = useState('')
  const [editingIncident, setEditingIncident] = useState<IncidentResponse | null>(null)
  const [creatingForSession, setCreatingForSession] = useState<SessionInfoResponse | null>(null)
  const [viewingSessionKey, setViewingSessionKey] = useState<string | null>(null)
  const [createDialogKey, setCreateDialogKey] = useState(0)

  const month = selectedMonth.format('YYYY-MM')

  const viewingOtherUser = isAdmin && !!selectedUserId
  const ownSummary = useSessionSummary(viewingOtherUser ? '' : month)
  const adminSummary = useAdminSessionSummary(viewingOtherUser ? selectedUserId : '', month)
  const { data: summary, isLoading: sessionsLoading, isError } = viewingOtherUser ? adminSummary : ownSummary

  const sessions = summary?.sessions ?? []

  // Live lookup — always reads from the latest cache, not a stale snapshot
  const viewingSession = viewingSessionKey
    ? sessions.find(s => s.startTime === viewingSessionKey) ?? null
    : null

  const totalMinutes                = summary?.totalMinutes                ?? 0
  const paidMinutes                 = summary?.paidMinutes                 ?? 0
  const weekendMinutes              = summary?.weekendMinutes              ?? 0
  const workHoursStart              = summary?.workHoursStart              ?? 9
  const workHoursEnd                = summary?.workHoursEnd                ?? 18
  const incidentInvestigationMinutes = summary?.incidentInvestigationMinutes ?? 0

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language.startsWith('ru') ? 'ru' : 'en'}>
      <Box>
        {/* Controls */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
                {t('sessions.title')}
              </Typography>
              {isAdmin && (
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <Select
                    value={selectedUserId}
                    displayEmpty
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    renderValue={(v) => v
                      ? (adminUsers ?? []).find((u) => u.userId === v)?.name ?? 'User'
                      : t('sessions.me')}
                  >
                    <MenuItem value="">{t('sessions.me')}</MenuItem>
                    {(adminUsers ?? []).filter((u) => u.email !== account?.email).map((u) => (
                      <MenuItem key={u.userId} value={u.userId}>{u.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              <DatePicker
                label={t('sessions.month')}
                value={selectedMonth}
                onChange={(v) => v && setSelectedMonth(v.startOf('month'))}
                views={['year', 'month']} openTo="month"
                // Short month name — "September 2026" gets clipped in the field.
                format="MMM YYYY"
                timezone={userTz}
                slotProps={{ textField: { size: 'small', sx: { width: 150 } } }}
              />
              <Button variant="contained" startIcon={<DownloadIcon />}
                onClick={() => {
                  const m = selectedMonth.format('YYYY-MM')
                  const fn = viewingOtherUser
                    ? () => downloadAdminExcel(selectedUserId, m)
                    : () => downloadExcel(m)
                  fn().catch(console.error)
                }}
                disabled={!sessions || sessionsLoading}>
                {t('sessions.exportExcel')}
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {isError && <Alert severity="error" sx={{ mb: 3 }}>{t('sessions.loadError', { month: selectedMonth.format('MMMM YYYY') })}</Alert>}

        {/* Stats */}
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
          <StatCard
            label={t('sessions.stats.totalOnCall')}
            value={formatDuration(totalMinutes)}
            icon={<AccessTimeIcon fontSize="inherit" />}
            loading={sessionsLoading}
          />
          <StatCard
            label={t('sessions.stats.paidHours', { start: workHoursStart, end: workHoursEnd })}
            value={formatDuration(paidMinutes)}
            icon={<PaidIcon fontSize="inherit" />}
            color="success.main"
            loading={sessionsLoading}
          />
          <StatCard
            label={t('sessions.stats.weekendHours')}
            value={formatDuration(weekendMinutes)}
            icon={<WeekendIcon fontSize="inherit" />}
            color="warning.main"
            loading={sessionsLoading}
          />
          <StatCard
            label={t('sessions.stats.investigationTime')}
            value={incidentInvestigationMinutes > 0 ? formatDuration(incidentInvestigationMinutes) : '—'}
            icon={<ManageSearchIcon fontSize="inherit" />}
            color="#FB8C00"
            loading={sessionsLoading}
          />
        </Stack>

        {/* Sessions calendar */}
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography variant="h6">
                {t('sessions.stats.sessionCount', { count: sessions?.length ?? 0 })}
              </Typography>
              <Chip label={selectedMonth.format('MMMM YYYY')} size="small" color="primary" variant="outlined" />
            </Stack>
            <Box data-no-swipe>
              <SessionCalendar
                sessions={sessions}
                month={selectedMonth}
                onMonthChange={setSelectedMonth}
                tz={userTz}
                loading={sessionsLoading}
                onOpenSession={(s) => setViewingSessionKey(s.startTime)}
                onAddIncident={(s) => { setCreateDialogKey((k) => k + 1); setCreatingForSession(s) }}
              />
            </Box>
          </CardContent>
        </Card>
      </Box>

      <SessionIncidentsDialog
        session={viewingSession}
        tz={userTz}
        onClose={() => setViewingSessionKey(null)}
        onAdd={() => { setCreateDialogKey(k => k + 1); setCreatingForSession(viewingSession); }}
        onEdit={(inc) => setEditingIncident(inc)}
      />
      <EditIncidentDialog incident={editingIncident} tz={userTz} onClose={() => setEditingIncident(null)} />
      <CreateIncidentDialog key={createDialogKey} session={creatingForSession} tz={userTz} onClose={() => setCreatingForSession(null)} />
    </LocalizationProvider>
  )
}
